import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography } from '@mui/joy';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../utils/shipmentStatusDisplay';
import {
  PIECE_MILESTONE_STATUS_ORDER,
  pieceMilestoneCount,
  pieceMilestoneTotal,
} from '../utils/piecesStatusSummary';

/**
 * Renders the five milestone counts from `pieces_status_summary`.
 *
 * @param {object} summary — `{ total, by_status, by_event_type }`
 * @param {boolean} [showZeros=false] — include chips where count is 0
 * @param {boolean} [showTotal=true] — show overall piece count chip
 * @param {string} [size='sm'] — Joy chip size
 */
export default function PiecesStatusSummaryChips({
  summary,
  showZeros = false,
  showTotal = true,
  size = 'sm',
}) {
  const { t } = useTranslation('ui');

  const milestones = useMemo(() => {
    if (!summary || !summary.by_status) return [];
    return PIECE_MILESTONE_STATUS_ORDER.filter((key) => {
      const count = pieceMilestoneCount(summary, key);
      return showZeros || count > 0;
    });
  }, [summary, showZeros]);

  if (!summary || !summary.by_status) return null;

  const total = pieceMilestoneTotal(summary);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {showTotal && (
        <Chip variant="soft" color="primary" size={size}>
          {t('shipmentPieces.summary.total')}: {total}
        </Chip>
      )}
      {milestones.map((key) => {
        const count = pieceMilestoneCount(summary, key);
        return (
          <Chip
            key={key}
            size={size}
            color={shipmentStatusJoyColor(key)}
            variant="soft"
            sx={shipmentStatusChipSx(key)}
          >
            {count} {shipmentStatusLabel(key, t)}
          </Chip>
        );
      })}
      {milestones.length === 0 && total === 0 && (
        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
          {t('shipmentPieces.noPieces')}
        </Typography>
      )}
    </Box>
  );
}
